/**
 * Error Response Type Definitions
 *
 * Supports three error formats from the API:
 * 1. Basic - Simple errors with just a message
 * 2. Snackbar - Errors with title and subtext
 * 3. Complex - Multiple error items with keys and messages
 */

// Basic error details (empty or simple object)
export type BasicErrorDetails = Record<string, never> | {}

// Snackbar error details with title and subtext
export interface SnackbarErrorDetails {
  title: string
  subtext: string
}

// Complex error details with multiple error items
// Keys are error codes, values are localized error messages
export type ComplexErrorDetails = Record<string, string>

// Main error response structure (new API format)
export interface ErrorResponse {
  code: string
  message: string
  details: BasicErrorDetails | SnackbarErrorDetails | ComplexErrorDetails
}

// Legacy error response format (for backward compatibility)
export interface LegacyErrorResponse {
  error?: string
  message?: string
  title?: string
  subtext?: string
  data?: {
    error?: string
    message?: string
    title?: string
    subtext?: string
  }
}

// Type guard to check if details contain title and subtext (Snackbar)
export function isSnackbarError(details: any): details is SnackbarErrorDetails {
  return (
    details !== null
    && typeof details === 'object'
    && 'title' in details
    && 'subtext' in details
    && typeof details.title === 'string'
    && typeof details.subtext === 'string'
  )
}

// Type guard to check if details is a complex error (multiple error items)
export function isComplexError(details: any): details is ComplexErrorDetails {
  if (!details || typeof details !== 'object') {
    return false
  }

  // Check if it's not a snackbar error
  if (isSnackbarError(details)) {
    return false
  }

  // Check if it has any string values (error messages)
  const keys = Object.keys(details)
  return keys.length > 0 && keys.every(key => typeof details[key] === 'string')
}

// Type guard to check if details is basic (empty or no relevant data)
export function isBasicError(details: any): details is BasicErrorDetails {
  return (
    !details
    || (typeof details === 'object' && Object.keys(details).length === 0)
    || (!isSnackbarError(details) && !isComplexError(details))
  )
}

// Error type discriminator
export type ErrorType = 'basic' | 'snackbar' | 'complex'

// Complex error item for display
export interface ErrorItem {
  key: string
  message: string
}
